import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader } from '../../shared/ui/Card';
import { Button } from '../../shared/ui/Button';
import { LoadingSpinner } from '../../shared/ui/LoadingSpinner';
import { Badge } from '../../shared/ui/Badge';
import { errorLogger } from '../../utils/errorLogger';
import { Upload, FileText, Brain, AlertTriangle, CheckCircle, X } from 'lucide-react';

interface DocumentAnalysis {
  id: string;
  filename: string;
  documentType: 'lab_report' | 'prescription' | 'medical_record' | 'imaging' | 'other';
  status: 'processing' | 'completed' | 'failed';
  uploadedAt: string;
  completedAt?: string;
  analysis?: {
    summary: string;
    keyFindings: string[];
    recommendations: string[];
    riskFactors: string[];
    followUpActions: string[];
    confidence: number;
    extractedData: {
      [key: string]: any;
    };
  };
  error?: string;
}

interface AnalysisResult {
  summary: string;
  keyFindings: string[];
  recommendations: string[];
  riskFactors: string[];
  followUpActions: string[];
  confidence: number;
  extractedData: {
    [key: string]: any;
  };
}

const DocumentAnalysis: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentAnalysis[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<'lab_report' | 'prescription' | 'medical_record' | 'imaging' | 'other'>('lab_report');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/jpg',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid file type (PDF, image, or document).');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const uploadDocument = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('documentType', documentType);

      const response = await fetch('/api/v1/ai/documents/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const newDocument: DocumentAnalysis = {
          id: data.id,
          filename: selectedFile.name,
          documentType,
          status: 'processing',
          uploadedAt: new Date().toISOString(),
        };

        setDocuments(prev => [newDocument, ...prev]);
        setSelectedFile(null);
        setDocumentType('lab_report');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Start polling for analysis completion
        pollAnalysisStatus(data.id);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      errorLogger.error('Failed to upload document', {
        component: 'DocumentAnalysis',
        error,
        context: { filename: selectedFile?.name, documentType }
      });
      alert('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const pollAnalysisStatus = async (documentId: string) => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`/api/v1/ai/documents/${documentId}/status`);
        if (response.ok) {
          const data = await response.json();
          
          setDocuments(prev => prev.map(doc => 
            doc.id === documentId 
              ? { ...doc, ...data }
              : doc
          ));

          if (data.status === 'completed' || data.status === 'failed') {
            return; // Stop polling
          }
        }
      } catch (error) {
        errorLogger.error('Failed to poll analysis status', {
          component: 'DocumentAnalysis',
          error,
          context: { documentId }
        });
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 10000); // Poll every 10 seconds
      }
    };

    poll();
  };

  const analyzeDocument = async (documentId: string) => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`/api/v1/ai/documents/${documentId}/analyze`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        
        setDocuments(prev => prev.map(doc => 
          doc.id === documentId 
            ? { ...doc, analysis: data.analysis, status: 'completed', completedAt: new Date().toISOString() }
            : doc
        ));
      } else {
        throw new Error('Analysis failed');
      }
    } catch (error) {
      errorLogger.error('Failed to analyze document', {
        component: 'DocumentAnalysis',
        error,
        context: { documentId }
      });
      alert('Failed to analyze document. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const deleteDocument = async (documentId: string) => {
    try {
      const response = await fetch(`/api/v1/ai/documents/${documentId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      errorLogger.error('Failed to delete document', {
        component: 'DocumentAnalysis',
        error,
        context: { documentId }
      });
      alert('Failed to delete document. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <LoadingSpinner size="sm" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <X className="w-5 h-5 text-red-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'lab_report':
        return 'Lab Report';
      case 'prescription':
        return 'Prescription';
      case 'medical_record':
        return 'Medical Record';
      case 'imaging':
        return 'Imaging Report';
      case 'other':
        return 'Other';
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">AI Document Analysis</h1>
        <div className="flex items-center space-x-2">
          <Brain className="w-6 h-6 text-blue-600" />
          <span className="text-sm text-gray-600">Powered by AI</span>
        </div>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Upload Document</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Document</label>
            <div className="flex items-center space-x-4">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png,.txt,.doc,.docx"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Choose File</span>
              </Button>
              {selectedFile && (
                <span className="text-sm text-gray-600">
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              )}
            </div>
          </div>

          {/* Document Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Document Type</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="lab_report">Lab Report</option>
              <option value="prescription">Prescription</option>
              <option value="medical_record">Medical Record</option>
              <option value="imaging">Imaging Report</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload Button */}
          <Button
            onClick={uploadDocument}
            disabled={isUploading || !selectedFile}
            className="w-full"
          >
            {isUploading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload & Analyze
              </>
            )}
          </Button>

          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Security & Privacy</p>
                <p className="mt-1">
                  Your documents are encrypted and processed securely. All data is protected under HIPAA guidelines.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Document History</h2>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No documents uploaded yet.
            </div>
          ) : (
            <div className="space-y-4">
              {documents.map((document) => (
                <div key={document.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(document.status)}
                      <div>
                        <h3 className="font-medium">{document.filename}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge className="bg-gray-100 text-gray-800">
                            {getDocumentTypeLabel(document.documentType)}
                          </Badge>
                          <Badge className={getStatusColor(document.status)}>
                            {document.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {document.status === 'failed' && (
                        <Button
                          onClick={() => analyzeDocument(document.id)}
                          disabled={isAnalyzing}
                          variant="outline"
                          size="sm"
                        >
                          {isAnalyzing ? <LoadingSpinner size="sm" /> : 'Retry'}
                        </Button>
                      )}
                      <Button
                        onClick={() => deleteDocument(document.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600">
                    Uploaded: {new Date(document.uploadedAt).toLocaleString()}
                    {document.completedAt && (
                      <span className="ml-4">
                        Completed: {new Date(document.completedAt).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Analysis Results */}
                  {document.analysis && (
                    <div className="mt-4 space-y-4">
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-2">Analysis Summary</h4>
                        <p className="text-gray-700 text-sm">{document.analysis.summary}</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium mb-2">Key Findings</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                            {document.analysis.keyFindings.map((finding, index) => (
                              <li key={index}>{finding}</li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium mb-2">Recommendations</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                            {document.analysis.recommendations.map((rec, index) => (
                              <li key={index}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {document.analysis.riskFactors.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Risk Factors</h4>
                          <div className="flex flex-wrap gap-2">
                            {document.analysis.riskFactors.map((risk, index) => (
                              <Badge key={index} className="bg-red-100 text-red-800">
                                {risk}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {document.analysis.followUpActions.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Follow-up Actions</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                            {document.analysis.followUpActions.map((action, index) => (
                              <li key={index}>{action}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm text-gray-600">
                          Confidence: {document.analysis.confidence}%
                        </span>
                        <Button variant="outline" size="sm">
                          View Full Report
                        </Button>
                      </div>
                    </div>
                  )}

                  {document.error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-800">Error: {document.error}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export { DocumentAnalysis };
